/**
 * defaultDeck.ts
 *
 * Scaffolds a complete presentation deck matching the production Celery
 * pipeline output.  Used by `SlideConfigStep` as fallback when the AI
 * generate-slides endpoint is unavailable or returns an incomplete deck,
 * and by `ensureRequiredSections` to patch missing sections into an
 * AI-generated deck.
 *
 * The target structure (39 slides in prod "Loreal Deportes"):
 *
 *   Portada (1) → Ebombo (2) → Concepto (2) → Invitaciones (1) →
 *   Minuto a Minuto + Journey (3) → Actividades (5) → Juegos (6) →
 *   Almuerzo (1) → Decoración/Equipment (7) → Zona Instagrameable (2) →
 *   Ubicación (1) → Renders (7) → Cierre (1)
 *
 * Product slides reference slugs from `backend/providers/base_product_catalog.py`.
 */

import type { SlideConfig } from '@/types/wizardV2';

// ─── Equipment slug mapping ──────────────────────────────────────────────────

export const EQUIPMENT_SLUG_MAP: Record<string, string> = {
  'Cámara 360': 'camara_360',
  'Photobooth': 'photobooth',
  'Cabina Espejada': 'espejo_magico',
  'Cubos Volumétrico': 'cubos_volumetricos',
  'Backing Banner': 'backing_banner',
  'Banner Rollup': 'banner_rollup',
} as const;

export const EQUIPMENT_SECTIONS = [
  'Cámara 360',
  'Photobooth',
  'Cabina Espejada',
  'Cubos Volumétrico',
  'Backing Banner',
  'Banner Rollup',
] as const;

export const COST_SECTIONS = [
  'Plan del Evento',
  'Costos',
  'Adicionales',
  'Resumen de Costos',
] as const;

// ─── Required sections for ensureRequiredSections ────────────────────────────

// Sections that ensureRequiredSections will inject if missing.
// "Ebombo" and "Invitaciones" are intentionally excluded — the AI scaffold
// places hero_stats, hero_client_logos, and invitation_showcase under
// varying section names (e.g. "Concepto"), so requiring them by exact name
// causes duplicates at the end of the deck.
const REQUIRED_SECTIONS = [
  'Portada',
  'Concepto',
  'Minuto a Minuto',
  'Journey del evento',
  'Actividades de Integración',
  'Decoración',
  'Zona Instagrameable',
  'Ubicación',
  'Renders',
  'Costos',
  'Cierre',
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function divider(section: string, title?: string): SlideConfig {
  const t = title ?? section;
  return {
    component: 'hero_section',
    section,
    title: t,
    subtitle: '',
    blocks: [{ type: 'title', text: t }],
    background: { type: 'plantilla_color', plantilla_index: 0 },
    images: [],
  };
}

function contentSlide(
  section: string,
  side: 'right' | 'left',
  title: string,
  body: string,
  bullets: string[],
  bgIdx = 2,
): SlideConfig {
  return {
    component: side === 'right' ? 'content_image_right' : 'content_image_left',
    section,
    title,
    subtitle: '',
    blocks: [
      { type: 'title', text: title },
      { type: 'body', text: body },
      { type: 'bullets', items: bullets.map((t) => ({ text: t })) },
    ],
    background: { type: 'plantilla_color', plantilla_index: bgIdx },
    images: [],
  };
}

function productPair(
  section: string,
  slug1: string,
  slug2: string,
  bgIdx = 2,
): SlideConfig {
  return {
    component: 'product_showcase_pair',
    section,
    title: '',
    subtitle: '',
    blocks: [],
    background: { type: 'plantilla_color', plantilla_index: bgIdx },
    images: [],
    items: [
      { name: '', product_slug: slug1 },
      { name: '', product_slug: slug2 },
    ],
  };
}

// ─── Main scaffolder ─────────────────────────────────────────────────────────

export function buildDefaultDeck(
  eventName: string,
  clientName: string,
  venue?: string,
): SlideConfig[] {
  const slides: SlideConfig[] = [];
  const venueLabel = venue ? ` en ${venue}` : '';
  const name = eventName || 'Evento Corporativo';
  const client = clientName || '';

  // ── 1. Portada ──────────────────────────────────────────────────────────
  slides.push({
    component: 'hero_cover',
    section: 'Portada',
    title: name,
    subtitle: client,
    blocks: [
      { type: 'title', text: name },
      { type: 'subtitle', text: client },
    ],
    background: { type: 'plantilla_color', plantilla_index: 0 },
    images: [],
  });

  // ── 2. Ebombo (hero_stats + hero_client_logos) ──────────────────────────
  slides.push({
    component: 'hero_stats',
    section: 'Ebombo',
    title: '',
    subtitle: '',
    blocks: [],
    background: { type: 'plantilla_color', plantilla_index: 1 },
    images: [],
  });

  slides.push({
    component: 'hero_client_logos',
    section: 'Ebombo',
    title: '',
    subtitle: '',
    blocks: [],
    background: { type: 'plantilla_color', plantilla_index: 1 },
    images: [],
  });

  // ── 3. Concepto (divider + concepto_full) ───────────────────────────────
  slides.push(divider('Concepto'));

  slides.push({
    component: 'concepto_full',
    section: 'Concepto',
    title: '',
    subtitle: '',
    blocks: [
      { type: 'title', text: `${name.toUpperCase()}` },
      { type: 'subtitle', text: 'Propuesta integral para el evento' },
    ],
    background: { type: 'plantilla_color', plantilla_index: 2 },
    images: [],
  });

  // ── 4. Invitaciones ─────────────────────────────────────────────────────
  slides.push({
    component: 'invitation_showcase',
    section: 'Invitaciones',
    title: '',
    subtitle: '',
    blocks: [{ type: 'title', text: 'Invitaciones del Evento' }],
    background: { type: 'plantilla_color', plantilla_index: 1 },
    images: [
      { id: 'inv-0', prompt: '', url: null, style: 'content', aspect_ratio: '3:4' },
      { id: 'inv-1', prompt: '', url: null, style: 'content', aspect_ratio: '3:4' },
    ],
  });

  // ── 5. Minuto a Minuto + Journey ────────────────────────────────────────
  slides.push(divider('Minuto a Minuto'));

  slides.push({
    component: 'journey_overview',
    section: 'Journey del evento',
    title: '',
    subtitle: '',
    blocks: [
      { type: 'title', text: 'Journey del evento' },
      { type: 'timeline_step', label: 'Recepción y bienvenida', description: 'Check-in de invitados\nEntrega de materiales\nBriefing de seguridad' },
      { type: 'timeline_step', label: 'Apertura del evento', description: 'Bienvenida oficial\nPresentación del programa\nFormación de equipos' },
      { type: 'timeline_step', label: 'Actividades principales', description: 'Dinámicas de integración\nCompetencias deportivas\nTalleres interactivos' },
      { type: 'timeline_step', label: 'Almuerzo', description: 'Buffet gourmet\nEstaciones temáticas\nNetworking' },
      { type: 'timeline_step', label: 'Competencias y juegos', description: 'Torneos por equipos\nDesafíos de precisión\nRelevos y obstáculos' },
      { type: 'timeline_step', label: 'Premiación y cierre', description: 'Entrega de trofeos\nReconocimientos\nFoto grupal' },
    ],
    background: { type: 'plantilla_color', plantilla_index: 0 },
    images: [],
  });

  slides.push({
    component: 'data_timeline',
    section: 'Minuto a Minuto',
    title: '',
    subtitle: '',
    blocks: [
      { type: 'title', text: 'Minuto a Minuto' },
      { type: 'timeline_step', time: '09:00 – 09:30', label: 'Recepción y acreditación', description: 'Check-in de invitados, entrega de materiales y uniformes temáticos por equipo.' },
      { type: 'timeline_step', time: '09:30 – 12:00', label: 'Actividades e integración', description: 'Apertura oficial, dinámicas rompehielos, competencias deportivas y talleres.' },
      { type: 'timeline_step', time: '12:00 – 13:00', label: 'Almuerzo gourmet', description: 'Buffet premium con estaciones temáticas, opciones vegetarianas y bebidas.' },
      { type: 'timeline_step', time: '13:00 – 16:00', label: 'Premiación y cierre', description: 'Zona interactiva, cámara 360°, entrega de trofeos y foto grupal.' },
    ],
    background: { type: 'plantilla_color', plantilla_index: 2 },
    images: [],
  });

  // ── 6. Actividades de Integración (divider + 4 content alternating) ─────
  slides.push(divider('Actividades de Integración'));

  slides.push(contentSlide(
    'Actividades de Integración', 'right',
    'Actividad 1',
    'Dinámica grupal para romper el hielo y formar equipos.',
    ['Duración de 45 minutos', 'Formación de equipos mixtos', 'Incluye materiales deportivos', 'Coordinador por equipo'],
    2,
  ));

  slides.push(contentSlide(
    'Actividades de Integración', 'left',
    'Actividad 2',
    'Desafío de relevos con rotación de estaciones.',
    ['4 estaciones de relevos', 'Equipos de 8 personas', 'Sistema de puntuación acumulativa', 'Premiación por equipo'],
    1,
  ));

  slides.push(contentSlide(
    'Actividades de Integración', 'right',
    'Actividad 3',
    'Reto colaborativo de construcción con materiales reciclados.',
    ['Materiales reciclados y deportivos', 'Tiempo límite de 30 minutos', 'Evaluación por creatividad', 'Exposición de resultados'],
    2,
  ));

  slides.push(contentSlide(
    'Actividades de Integración', 'left',
    'Actividad 4',
    'Carrera de obstáculos con apoyo de equipo.',
    ['Circuito de 6 obstáculos progresivos', 'Apoyo mutuo entre compañeros', 'Fotografía de acción', 'Premiación por tiempo'],
    2,
  ));

  // ── 7. Juegos y Competencias (divider + 5 content alternating) ──────────
  slides.push(divider('Juegos y Competencias'));

  slides.push(contentSlide(
    'Juegos y Competencias', 'right',
    'Competencia 1',
    'Competencia clásica de fuerza y coordinación por equipos.',
    ['Equipos balanceados', 'Eliminatorias hasta campeón', 'Premiación inmediata', 'Barra de apoyo por equipo'],
    2,
  ));

  slides.push(contentSlide(
    'Juegos y Competencias', 'left',
    'Competencia 2',
    'Carrera divertida con elementos deportivos.',
    ['Pista de 30 metros', 'Relevos por equipos de 4', 'Categorías por área', 'Trofeo al equipo ganador'],
    1,
  ));

  slides.push(contentSlide(
    'Juegos y Competencias', 'right',
    'Competencia 3',
    'Trivia sobre la empresa con sistema digital.',
    ['50 preguntas sobre la empresa', 'Sistema de respuesta digital', 'Premios por respuestas correctas', 'Rondas eliminatorias'],
    2,
  ));

  slides.push(contentSlide(
    'Juegos y Competencias', 'left',
    'Competencia 4',
    'Relevos acuáticos con estaciones de transporte.',
    ['Circuito de 4 estaciones', 'Transporte de agua con implementos', 'Medición por equipo', 'Premiación al mejor tiempo'],
    1,
  ));

  slides.push(contentSlide(
    'Juegos y Competencias', 'right',
    'Competencia 5',
    'Competencia de puntería y precisión con diferentes objetivos.',
    ['5 estaciones de puntería', 'Pelotas, aros y dardos', 'Puntuación acumulativa', 'Final entre los mejores'],
    2,
  ));

  // ── 8. Almuerzo (1 content slide, no divider) ──────────────────────────
  slides.push(contentSlide(
    'Almuerzo', 'left',
    `Catering${venueLabel}`,
    'Propuesta culinaria para el evento.',
    ['Comida para todos los asistentes', 'Estaciones temáticas', 'Servicio completo incluido', 'Opciones para restricciones alimentarias'],
    1,
  ));

  // ── 9. Decoración (divider + equipment content + product pairs) ─────────
  slides.push(divider('Decoración'));

  // Cámara 360 — content slide
  slides.push(contentSlide(
    'Decoración', 'right',
    'Cámara 360',
    'Plataforma giratoria de video 360° con brazo mecánico.',
    ['Plataforma para 1-4 personas', 'Video slow-motion 360°', 'Overlay personalizado con logo', 'Descarga inmediata a celular'],
    2,
  ));

  // Photobooth + Espejo Mágico — product pair
  slides.push(productPair('Decoración', 'photobooth', 'espejo_magico', 2));

  // Arco de Entrada — content slide
  slides.push(contentSlide(
    'Decoración', 'left',
    'Arco de Entrada',
    'Arco deportivo con branding corporativo.',
    ['Branding corporativo integrado', 'Iluminación LED', 'Punto focal para fotos', 'Instalación y desinstalación incluida'],
    2,
  ));

  // Cubos Volumétricos — content slide
  slides.push(contentSlide(
    'Decoración', 'right',
    'Cubos Volumétricos',
    'Cubos volumétricos con branding e iluminación LED.',
    ['Cubos de 30×30 y 50×50 cm', 'Forrado en vinil con branding', 'Iluminación LED RGB', 'Ubicación estratégica en el evento'],
    1,
  ));

  // Backing Banner + Banner Rollup — product pair
  slides.push(productPair('Decoración', 'backing_banner', 'banner_rollup', 2));

  // Letras Gigantes + Banner Largo — product pair
  slides.push(productPair('Decoración', 'letras_gigantes', 'banner_largo', 1));

  // ── 10. Zona Instagrameable (divider + showcase) ────────────────────────
  slides.push(divider('Zona Instagrameable'));

  slides.push({
    component: 'zona_instagrameable_showcase',
    section: 'Zona Instagrameable',
    title: '',
    subtitle: '',
    blocks: [{ type: 'title', text: 'Zona Instagrameable' }],
    background: { type: 'plantilla_color', plantilla_index: 1 },
    images: [],
  });

  // ── 11. Ubicación (single showcase, no divider) ─────────────────────────
  slides.push({
    component: 'showcase_venue',
    section: 'Ubicación',
    title: '',
    subtitle: '',
    blocks: [{ type: 'title', text: venue ? `${venue}: Espacio Premium` : 'Ubicación del Evento' }],
    background: { type: 'plantilla_color', plantilla_index: 1 },
    images: [],
  });

  // ── 12. Renders (divider + 6 render_showcase) ──────────────────────────
  slides.push(divider('Renders', 'Renders del Evento'));

  const renderTitles = [
    `Entrada Principal${venueLabel}`,
    'Salón Principal Decorado',
    'Zona de Competencias',
    'Zona Fotográfica Interactiva',
    'Área de Catering',
    'Ceremonia de Premiación',
  ];

  for (let r = 0; r < renderTitles.length; r++) {
    slides.push({
      component: 'render_showcase',
      section: 'Renders',
      title: '',
      subtitle: '',
      blocks: [{ type: 'title', text: renderTitles[r] }],
      background: { type: 'plantilla_color', plantilla_index: r % 2 === 0 ? 2 : 1 },
      images: [],
    });
  }

  // ── 13. Costos (divider + cost table) ────────────────────────────────
  slides.push(divider('Costos', 'Costos del Evento'));

  slides.push({
    component: 'data_cost_table',
    section: 'Costos',
    title: 'Costos del Evento',
    subtitle: '',
    blocks: [],
    background: { type: 'plantilla_color', plantilla_index: 0 },
    images: [],
    rows: [],
    total_row: { label: 'Total', client_price: 0 },
  });

  // ── 14. Cierre ──────────────────────────────────────────────────────────
  slides.push({
    component: 'hero_closing',
    section: 'Cierre',
    title: '',
    subtitle: '',
    blocks: [
      { type: 'title', text: name },
      { type: 'subtitle', text: client },
    ],
    background: { type: 'plantilla_color', plantilla_index: 9 },
    images: [],
  });

  return slides;
}

// ─── Section enforcement ─────────────────────────────────────────────────────

export function ensureRequiredSections(
  aiSlides: SlideConfig[],
  eventName: string,
  clientName: string,
  venue?: string,
): SlideConfig[] {
  const lower = (s: string) => s.toLowerCase().trim();
  const existingLower = new Set(aiSlides.map((s) => lower(s.section)));

  const missingSections = REQUIRED_SECTIONS.filter((s) => !existingLower.has(lower(s)));

  if (missingSections.length === 0) return aiSlides;

  const defaultDeck = buildDefaultDeck(eventName, clientName, venue);

  // Group default slides by section for lookup.
  const defaultBySection = new Map<string, SlideConfig[]>();
  for (const s of defaultDeck) {
    const key = lower(s.section);
    if (!defaultBySection.has(key)) defaultBySection.set(key, []);
    defaultBySection.get(key)!.push(s);
  }

  // Build a section-order index from REQUIRED_SECTIONS so we can
  // insert each missing section at its natural position relative to
  // sections that already exist in the AI deck.
  const sectionOrder = new Map<string, number>();
  REQUIRED_SECTIONS.forEach((s, i) => sectionOrder.set(lower(s), i));

  const result = [...aiSlides];

  for (const missing of missingSections) {
    const missingLower = lower(missing);
    const missingOrder = sectionOrder.get(missingLower) ?? 999;
    const slidesToInsert = defaultBySection.get(missingLower) ?? [];
    if (slidesToInsert.length === 0) continue;

    // Find the right insertion point: after the last slide whose section
    // has a lower order index, or before the first slide whose section
    // has a higher order index.
    let insertAt = result.length;
    for (let i = 0; i < result.length; i++) {
      const slideSection = lower(result[i].section);
      const slideOrder = sectionOrder.get(slideSection);
      if (slideOrder !== undefined && slideOrder > missingOrder) {
        insertAt = i;
        break;
      }
    }

    result.splice(insertAt, 0, ...slidesToInsert);
  }

  return result;
}
